import { ROUTES } from 'utils/constants';

/** Persisted in encrypted active-company storage when the user selects all companies. */
export const ALL_COMPANIES_SELECTION_ID = '__all_companies__';

/** Settings routes available while all-companies mode is active (user-level only). */
export const ALL_COMPANIES_SIDEBAR_SETTINGS_PATHS = [
  ROUTES.SETTINGS.GENERAL,
  ROUTES.SETTINGS.NOTIFICATIONS,
  ROUTES.SETTINGS.PROFILE,
  ROUTES.SETTINGS.COMPANIES,
] as const;

const ALL_COMPANIES_ALLOWED_SETTINGS_PATHS = [
  ...ALL_COMPANIES_SIDEBAR_SETTINGS_PATHS,
  ROUTES.SETTINGS.CHANGE_PASSWORD,
] as const;

/** User-scoped routes (not company-scoped) available in all-companies mode. */
const ALL_COMPANIES_ALLOWED_USER_PATHS = [
  ROUTES.NOTIFICATIONS,
  ROUTES.SUPERADMIN.ROOT,
  /** Platform Super Admin Hub (`/super-admin/*`), distinct from `/superadmin`. */
  ROUTES.SUPER_ADMIN.ROOT,
  ROUTES.DOWNLOADABLES,
] as const;

const SUPER_DASHBOARD_PATHS = [
  ROUTES.SUPER_DASHBOARD,
  ROUTES.SUPER_DASHBOARD_HEALTH_CHECKS,
  ROUTES.SUPER_DASHBOARD_TAX_REMINDERS,
  ROUTES.SUPER_DASHBOARD_UPLOAD_ENTRIES,
  ROUTES.SUPER_DASHBOARD_UPLOADED_ENTRIES_EMAIL,
  ROUTES.SUPER_DASHBOARD_ACTIVITY_LOGS,
] as const;

export function isAllCompaniesSelectionActive(
  activeCompanyId: string | null | undefined,
): boolean {
  return activeCompanyId === ALL_COMPANIES_SELECTION_ID;
}

export function canSelectAllCompanies(
  companyCount: number,
  showAllCompaniesEligible = true,
): boolean {
  return companyCount > 1 && showAllCompaniesEligible;
}

/** Real company UUID for API scoping, or null when all-companies mode is active. */
export function resolveScopedCompanyId(
  activeCompanyId: string | null | undefined,
): string | null {
  if (!activeCompanyId || isAllCompaniesSelectionActive(activeCompanyId)) {
    return null;
  }
  return activeCompanyId;
}

export function isSuperDashboardPath(pathname: string): boolean {
  const clean = pathname.split('?')[0].split('#')[0].replace(/\/$/, '') || '/';
  return SUPER_DASHBOARD_PATHS.some(
    (path) => clean === path || clean.startsWith(`${path}/`),
  );
}

export function isAllCompaniesAllowedPath(pathname: string): boolean {
  const clean = pathname.split('?')[0].split('#')[0].replace(/\/$/, '') || '/';

  if (isSuperDashboardPath(clean)) {
    return true;
  }

  if (clean === ROUTES.SETTINGS.ROOT) {
    return true;
  }

  if (
    ALL_COMPANIES_ALLOWED_USER_PATHS.some(
      (allowedPath) =>
        clean === allowedPath || clean.startsWith(`${allowedPath}/`),
    )
  ) {
    return true;
  }

  return ALL_COMPANIES_ALLOWED_SETTINGS_PATHS.some(
    (allowedPath) =>
      clean === allowedPath || clean.startsWith(`${allowedPath}/`),
  );
}
